import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import "../../assets/styles/colors.css";

function JsonPreviewModal({ show, nodes, edges, topologyMeta, onClose }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const topologyJson = useMemo(() => {
    if (!show) return "";
    const nodeCount = Array.isArray(nodes) ? nodes.length : 0;
    const edgeCount = Array.isArray(edges) ? edges.length : 0;
    const typeCounts = nodes.reduce((acc, node) => {
      const rawType = node?.data?.nodeType || node?.type || "device";
      const key = String(rawType).trim().toLowerCase() || "device";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const topTypes = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([type, count]) => `${count} ${type.replaceAll("_", " ")}`);
    const dominantType = topTypes[0]?.split(" ").slice(1).join(" ");
    const firstLabel =
      nodes.find((node) => node?.data?.label)?.data?.label ||
      nodes[0]?.id ||
      null;
    const inferredTitle = dominantType
      ? `${dominantType
          .split(" ")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")} Topology`
      : firstLabel
        ? `${String(firstLabel).slice(0, 50)} Topology`
      : `Network Topology (${nodeCount} nodes)`;
    const inferredDescription = topTypes.length
      ? `Topology with ${nodeCount} nodes and ${edgeCount} edges. Main device mix: ${topTypes.join(", ")}.`
      : `Topology with ${nodeCount} nodes and ${edgeCount} edges.`;

    const resolvedTitle =
      topologyMeta?.title ||
      topologyMeta?.name ||
      topologyMeta?.ai_title ||
      inferredTitle;
    const resolvedDescription =
      topologyMeta?.description ||
      topologyMeta?.ai_description ||
      inferredDescription;
    const resolvedAiTitle =
      topologyMeta?.ai_title || resolvedTitle;
    const resolvedAiDescription =
      topologyMeta?.ai_description ||
      resolvedDescription;
    const includeAiTitle = Boolean(
      topologyMeta?.ai_title?.trim(),
    );
    const includeAiDescription = Boolean(
      topologyMeta?.ai_description?.trim(),
    );

    return JSON.stringify(
      {
        // Keep both title and name for backward compatibility with older imports.
        title: resolvedTitle,
        name: resolvedTitle,
        version: topologyMeta?.version || "1.0",
        description: resolvedDescription,
        ...(includeAiTitle ? { ai_title: resolvedAiTitle } : {}),
        ...(includeAiDescription ? { ai_description: resolvedAiDescription } : {}),
        metadata: {
          created_at:
            topologyMeta?.metadata?.created_at || new Date().toISOString(),
          created_by: topologyMeta?.metadata?.created_by || "user",
          environment: topologyMeta?.metadata?.environment || "mvp",
          exported_at: new Date().toISOString(),
        },
        nodes: nodes.map((node) => ({
          id: node.id,
          type: node.type || "device",
          position: node.position
            ? { x: Math.round(node.position.x), y: Math.round(node.position.y) }
            : { x: 0, y: 0 },
          data: { ...node.data },
        })),
        edges: edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: edge.type || "smoothstep",
          label: edge.label || "",
          data: { ...edge.data },
        })),
      },
      null,
      2,
    );
  }, [show, nodes, edges, topologyMeta]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(topologyJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([topologyJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "topology.json";
    a.click();
    URL.revokeObjectURL(url);
  };


  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl mx-4 rounded-xl border border-[var(--color-border)] bg-white dark:bg-(--color-bg) shadow-lg dark:shadow-[0_0_20px_var(--color-shadow-primary)]"
>
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-[var(--color-border)] px-6 py-4 bg-slate-50 dark:bg-(--color-bg)">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--color-text-white)]">
              {t("Preview JSON")}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
              {nodes.length} {t("nodes")} • {edges.length} {t("edges")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                copied
                  ? "border-green-500 bg-green-500/20 text-green-600 dark:text-green-400"
                  : "border-slate-300 dark:border-[var(--color-primary)]/30 text-slate-700 dark:text-[var(--color-primary)] hover:bg-slate-100 dark:hover:bg-[var(--color-primary)]/10"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={copied ? 2 : 1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={
                    copied
                      ? "M5 13l4 4L19 7"
                      : "M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                  }
                />
              </svg>
              {copied ? t("Copied!") : t("Copy")}
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--color-primary)] border border-[var(--color-primary)] text-white dark:text-black rounded-lg text-sm font-semibold hover:shadow-lg dark:hover:shadow-[0_0_10px_var(--color-shadow-primary-hover)] transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              {t("Download")}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-[var(--color-text-white)] hover:bg-slate-100 dark:hover:bg-[var(--color-primary)]/10 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 max-h-[60vh] overflow-auto">
          <pre className="p-4 bg-slate-100 dark:bg-black/50 border border-slate-300 dark:border-[var(--color-border)] rounded-xl text-slate-900 dark:text-[var(--color-text-white)] text-xs font-mono whitespace-pre-wrap overflow-x-auto">
            {topologyJson}
          </pre>
        </div>

        <div className="border-t border-slate-200 dark:border-[var(--color-border)] px-6 py-3 bg-slate-50 dark:bg-(--color-bg)">
          <p className="text-xs text-slate-600 dark:text-gray-400">
            {t("ReactFlow-compatible format with all custom data preserved.")}
          </p>
        </div>
      </div>
    </div>
  );
}

JsonPreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  nodes: PropTypes.array.isRequired,
  edges: PropTypes.array.isRequired,
  topologyMeta: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default JsonPreviewModal;
